import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { orderBy, slice, sumBy } from 'lodash'
import PropTypes from 'prop-types'
import { Component } from 'react'
import { FormattedMessage } from 'react-intl'

import { Popover } from '@/components/TailwindUI/Popover'
import HorizontalBarChart from '@/pages/SpaceAnalyticsPage/charts/HorizontalBarChart'
import pageMessages from '@/pages/SpaceAnalyticsPage/messages'
import DetailsViewedIcon from '@/pages/SpaceAnalyticsPage/util/icons/DetailsViewedIcon'
import MessagesIcon from '@/pages/SpaceAnalyticsPage/util/icons/MessagesIcon'
import TotalClickedIcon from '@/pages/SpaceAnalyticsPage/util/icons/TotalClicksIcons'
import UniqueViewsIcon from '@/pages/SpaceAnalyticsPage/util/icons/UniqueViewsIcon'
import MetricHorizontalSection from '@/pages/SpaceAnalyticsPage/util/MetricHorizontalSection'
import sumConnectorData from '@/pages/SpaceAnalyticsPage/util/sumConnectorData'

import messages from './messages'

class IntegrationsSection extends Component<any, any> {
  static displayName = 'IntegrationsSection'

  static propTypes = {
    connectorActivity: PropTypes.array.isRequired
  }

  getConnectorName = (item: any) =>
    item.connectorName && item.connectorName !== '' ? item.connectorName : item.sourceTitle

  calculatePercent = (total: any, item: any) => {
    return ((item / total) * 100).toFixed(2)
  }

  renderTooltip = ({ active, payload }: any) => {
    if (active && payload[0]) {
      return (
        <div className="chart integrations floating-tooltip">
          <div className="header-holder">
            <h3>{payload[0].payload.name}</h3>
          </div>
          <div className="data-holder">
            <table>
              <tbody>
                <tr>
                  <td>
                    <FormattedMessage {...messages.totalSentLabel} />
                  </td>
                  <td>{payload[0].payload.detail.sentNotifications}</td>
                </tr>
                <tr>
                  <td>
                    <FormattedMessage {...messages.viewedNotificationsLabel} />
                  </td>
                  <td>{payload[0].payload.detail.viewedNotifications}</td>
                </tr>
                <tr>
                  <td>
                    <FormattedMessage {...messages.detailViewsLabel} />
                  </td>
                  <td>{payload[0].payload.detail.detailViewed}</td>
                </tr>
                <tr>
                  <td>
                    <FormattedMessage {...messages.uniqueClicksLabel} />
                  </td>
                  <td>{payload[0].payload.detail.clickThroughRate}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    }
  }

  /**
   * Creates an ordered array of the top 10 connectors with the most popular connector
   * first
   *
   * @param connectorData
   * @returns {Array}
   */
  formatData = (connectorData: any) => {
    const total = sumBy(connectorData, item => {
      // @ts-expect-error
      return item.sentNotifications
    })
    return slice(
      orderBy(
        connectorData.map((item: any) => {
          return {
            name: this.getConnectorName(item),
            value: item.sentNotifications,
            detail: {
              ...item,
              percent: this.calculatePercent(total, item.sentNotifications)
            }
          }
        }),
        item => {
          return item.value
        },
        ['desc']
      ),
      0,
      10
    )
  }

  render() {
    const { connectorActivity } = this.props
    const formattedConnectorData = this.formatData(connectorActivity)
    const connectorDataTotals = sumConnectorData(connectorActivity)
    return (
      <div className="analytics-section">
        <div className="metric-card-subheader">
          <h3>
            <FormattedMessage {...messages.title} />
          </h3>

          {connectorActivity.length > 0 && (
            <span className="sub-text">
              <FormattedMessage {...messages.titleSubText} />
            </span>
          )}
          {connectorActivity.length > 0 ? (
            <div className="sub-section-wrapper">
              <div className="metric-horizontal-holder sub-section">
                <MetricHorizontalSection
                  icon={<MessagesIcon />}
                  label={<FormattedMessage {...messages.totalSentLabel} />}
                  value={connectorDataTotals.totalSent}
                  renderDifferenceValue={false}
                />
                <MetricHorizontalSection
                  icon={<UniqueViewsIcon />}
                  label={<FormattedMessage {...messages.totalViewedLabel} />}
                  value={connectorDataTotals.totalViewed}
                  renderDifferenceValue={false}
                />
                <MetricHorizontalSection
                  icon={<DetailsViewedIcon />}
                  label={<FormattedMessage {...messages.totalDetailsViewedLabel} />}
                  value={connectorDataTotals.totalDetailsViewed}
                  renderDifferenceValue={false}
                />
                <MetricHorizontalSection
                  icon={<TotalClickedIcon />}
                  label={
                    <div className="flex items-center">
                      <FormattedMessage {...messages.totalClicksLabel} />{' '}
                      <Popover
                        tooltipMessage={`"Total clicks” includes all user clicks of hyperlinks, action buttons, and detail buttons on summary cards and detail panels.`}
                        icon={<InformationCircleIcon className="stroke-width-2 h-6 w-6 !fill-none" />}
                      />
                    </div>
                  }
                  value={connectorDataTotals.totalClicks}
                  renderDifferenceValue={false}
                />
              </div>
              <div className="divider" />
              <div className="metric-horizontal-holder sub-section">
                <div>
                  <FormattedMessage {...messages.usageByAppLabel} />
                </div>
                <span className="sub-text">
                  <FormattedMessage {...messages.hoverToViewDetailsLabel} />
                </span>
                <HorizontalBarChart data={formattedConnectorData} tooltip={this.renderTooltip} />
              </div>
            </div>
          ) : (
            <p className="flex-center">
              <FormattedMessage {...pageMessages.noActivityMetrics} />
            </p>
          )}
        </div>
      </div>
    )
  }
}

export default IntegrationsSection
